/**
 * Device eligibility for flow "Do" / device-action pickers.
 * Mirrors scene Do DeviceStep filters (category/type/shared/module) and requires
 * non-empty _init_action_components_v2 so the property/action UI is never empty.
 */

import type { Device } from 'src/types/Device';
import {
	excludedCategoriesFromDoActions,
	excludedDeviceTypesFromDoActions,
	includedDeviceTypesForDoActions,
} from 'src/components/Scenes/helpers/init_device_actions';
import { _init_action_components_v2 } from 'src/components/Scenes/SceneWizard/Process/Do/Device/helpers';
import { isCctOnlyDevice } from 'src/helpers/interface_helpers';

const excludeShared = true;

/**
 * Same category/type/shared/module rules as scene Do DeviceStep (flat device list, no room filter).
 */
export const isDeviceEligibleForDoDeviceStepFlatList = (device: Device | undefined | null): boolean => {
	if (!device?.id) {
		return false;
	}

	if (excludeShared && device.shared) {
		return false;
	}

	if (
		excludedCategoriesFromDoActions.length > 0 &&
		device.category &&
		excludedCategoriesFromDoActions.includes(device.category) &&
		!includedDeviceTypesForDoActions.includes(device.type)
	) {
		return false;
	}

	if (
		excludedDeviceTypesFromDoActions.length > 0 &&
		excludedDeviceTypesFromDoActions.includes(device.type)
	) {
		return false;
	}

	if (device.category === 'module') {
		if (isCctOnlyDevice(device.type)) {
			return true;
		}
		return !/^(inputs_reader|shell)$/.test(device.mode ?? '');
	}

	return true;
};

export const deviceHasDoActionComponents = (
	device: Device,
	status: unknown,
	settings: unknown,
): boolean => {
	try {
		const components = _init_action_components_v2(device, status, settings);
		return Array.isArray(components) && components.length > 0;
	} catch {
		return false;
	}
};

/**
 * Eligible per scene Do rules and exposes at least one action component (real "props").
 */
export const isSelectableForFlowDeviceAction = (
	device: Device | undefined | null,
	status: unknown,
	settings: unknown,
): boolean => {
	if (!isDeviceEligibleForDoDeviceStepFlatList(device)) {
		return false;
	}
	return deviceHasDoActionComponents(device!, status, settings);
};
